import PropTypes from 'prop-types';
import prepareTranslate from '@/helpers/dictionary';
import {
  TextInput,
  Button,
  Grid,
  Select,
  NumberInput,
} from '@mantine/core';
import validations from '@/helpers/validation';
import { useForm } from '@mantine/form';
import companyQuery from '@/api/queries/company';
import productQuery from '@/api/queries/product';
import { ACCORD_TYPE, PRODUCT_ENTITY } from '@/helpers/enums';
import Modal from '@/components/DetailsModal';
import { useDisclosure } from '@mantine/hooks';

const AlternativeFundEditForm = (props) => {
  const {
    alternativeFund, onCancel, onSave, onDelete, opened, close, isLoading,
  } = props;
  const t = prepareTranslate();

  const { data: companiesData } = companyQuery.useGetAll();
  const { data: productsData } = productQuery.useGetAll();
  const [deleteOpened, { open: deleteOpen, close: deleteClose }] = useDisclosure();

  const companiesOptions = companiesData?.map((company) => ({
    label: company.hebrew,
    value: company._id,
  })) || [];

  // eslint-disable-next-line max-len
  const productsOptions = productsData?.filter((product) => product.applicableTo.includes(PRODUCT_ENTITY.alternativeFund)).map((product) => ({
    label: product.hebrew,
    value: product._id,
  })) || [];

  const form = useForm({
    initialValues: {
      alternativeFundNumber: alternativeFund.alternativeFundNumber || '',
      uid: alternativeFund.uid || '',
      alternativeFundType: alternativeFund.alternativeFundType || '',
      accordType: alternativeFund.accordType || 'independent',
      agentCommission: alternativeFund.agentCommission || '',
      employeeAgentCommission: alternativeFund.employeeAgentCommission || '',
      companyId: alternativeFund.companyId._id.toString() || '',
      branchId: alternativeFund.branchId._id.toString() || '',
      alternativeFundName: alternativeFund.alternativeFundName || '',
      comments: alternativeFund.comments || '',
    },
    validate: {
      companyId: (value) => validations.required(value),
      accordType: (value) => validations.required(value),
      branchId: (value) => validations.required(value),
      alternativeFundNumber: (value) => validations.required(value),
    },
  });

  const alternativeFundAccordType = form.values.accordType;

  const handleSubmit = () => {
    onSave(form.values);
  };

  const handleDeleteAlternativeFund = () => {
    if (!form.values.deletionReason) {
      form.setFieldError('deletionReason', 'שדה חובה');
      return;
    }

    onDelete(form.values.deletionReason);
    deleteClose();
  };

  const handleCancel = () => {
    deleteClose();
  };

  const deleteActions = [
        <Button key='delete' w={'6rem'} onClick={handleDeleteAlternativeFund}>
            {t('global.delete')}
        </Button>,
        <Button key='cancel' w={'6rem'} onClick={handleCancel} variant='outline'>
            {t('global.cancel')}
        </Button>,
  ];

  const modalActions = [
        <Button key='save' loading={isLoading} onClick={handleSubmit}>
            {t('global.update')}
        </Button>,
        <Button key='cancel' variant='outline' onClick={onCancel}>
            {t('global.cancel')}
        </Button>,
  ];

  return (
        <Modal
            opened={opened}
            close={close}
            label={t('components.editAlternativeFunds.addAlternativeFund')}
            actions={modalActions}
        >
            <Grid gutter={{ base: 'xl' }}>
                <Grid.Col span={{ base: 3 }}>
                    <Select
                        label={t('components.editAlternativeFunds.company')}
                        key={form.key('companyId')}
                        data={companiesOptions}
                        required
                        {...form.getInputProps('companyId')}
                  />
                       <TextInput
                        label={t('components.editAlternativeFunds.alternativeFundType')}
                        key={form.key('alternativeFundType')}
                        {...form.getInputProps('alternativeFundType')}
                    />
                </Grid.Col>

                <Grid.Col span={{ base: 3 }}>
                    <Select
                        required
                        label={t('components.editAlternativeFunds.branch')}
                        data={productsOptions}
                        key={form.key('branchId')}
                        {...form.getInputProps('branchId')}
                    />
                                   <Select
                        required
                        label={t('components.editAlternativeFunds.accordType')}
                        key={form.key('accordType')}
                        defaultValue={ACCORD_TYPE[1].value}
                        data={ACCORD_TYPE}
                        {...form.getInputProps('accordType')}
                    />

                </Grid.Col>

                <Grid.Col span={{ base: 3 }}>
                    <TextInput
                        autoComplete='off'
                        label={t('components.editAlternativeFunds.number')}
                        key={form.key('alternativeFundNumber')}
                        required
                        {...form.getInputProps('alternativeFundNumber')}
                  />
                      {alternativeFundAccordType === 'independent' ? (
                        <NumberInput
                            autoComplete='off'
                            label={t('components.editAlternativeFunds.commission')}
                            key={form.key('agentCommission')}
                            {...form.getInputProps('agentCommission')}
                        />
                      ) : (
                        <NumberInput
                            autoComplete='off'
                            label={t('components.editAlternativeFunds.commission')}
                            key={form.key('employeeAgentCommission')}
                            {...form.getInputProps('employeeAgentCommission')}
                        />
                      )}
                </Grid.Col>

                <Grid.Col span={{ base: 3 }}>
                    <TextInput
                        label={t('components.editAlternativeFunds.alternativeFundName')}
                        key={form.key('alternativeFundName')}
                        {...form.getInputProps('alternativeFundName')}
                    />

                </Grid.Col>
            </Grid>

            <Grid>
                <Grid.Col>
                    <TextInput
                        label={t('components.editAlternativeFunds.comments')}
                        key={form.key('comments')}
                        maxLength={300}
                        {...form.getInputProps('comments')}
                    />
                </Grid.Col>
            </Grid>

            <Grid>
                <Grid.Col>
                    <Button variant='outline' onClick={deleteOpen}>
                        {t('components.editAlternativeFunds.deleteAlternativeFund')}
                    </Button>
                </Grid.Col>
            </Grid>
            <Modal
                opened={deleteOpened}
                close={handleCancel}
                label={t('components.editAlternativeFunds.verifyDeletion')}
                actions={deleteActions}
            >
                <TextInput
                    required
                    {...form.getInputProps('deletionReason')}
                    label={t('components.editAlternativeFunds.deletionReason')}
                />
            </Modal>
        </Modal>
  );
};

AlternativeFundEditForm.propTypes = {
  alternativeFund: PropTypes.object.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  opened: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default AlternativeFundEditForm;
